import { useCallback, useEffect, useState } from 'react';
import { Landing } from './components/Landing.tsx';
import { Player } from './components/Player.tsx';
import { CardsPage } from './pages/CardsPage.tsx';
import { LoginCallbackPage } from './pages/LoginCallbackPage.tsx';
import { StyleStudioPage } from './pages/StyleStudioPage.tsx';
import { LocalStorageSettingsStore } from './adapters/settings/LocalStorageSettingsStore.ts';
import { buildVideoSearch, readVideoIdFromSearch } from './lib/youtubeUrl.ts';
import { pushRecentVideo } from './lib/recentVideos.ts';
import { useThemeFromSettings } from './hooks/useThemeFromSettings.ts';
import type { UseYoutubePlayerOptions } from './hooks/useYoutubePlayer.ts';
import type { SubtitleProviderKind } from './adapters/subtitles/index.ts';

export type AppView = 'landing' | 'player' | 'cards' | 'login-callback' | 'style-studio';

export type AppProps = {
  /** Injected player options (fake YT in tests). */
  playerOptions?: UseYoutubePlayerOptions;
  /** Override initial search (tests). Default: window.location.search */
  initialSearch?: string;
  /** Override subtitle source (tests / `?subs=`). Default: sora. */
  initialProviderKind?: SubtitleProviderKind;
  /** Override initial view (tests). */
  initialView?: AppView;
  settingsStore?: LocalStorageSettingsStore;
};

function readProviderFromSearch(search: string): SubtitleProviderKind | undefined {
  const raw = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(
    'subs',
  );
  if (raw === 'sora' || raw === 'mock' || raw === 'upload') return raw;
  return undefined;
}

function readViewFromLocation(): AppView {
  if (typeof window === 'undefined') return 'landing';
  const path = window.location.pathname;
  if (path.includes('/login/callback')) return 'login-callback';
  if (path.endsWith('/style-studio') || window.location.hash === '#style-studio') {
    return 'style-studio';
  }
  if (path.endsWith('/cards')) return 'cards';
  if (window.location.hash === '#cards') return 'cards';
  return readVideoIdFromSearch(window.location.search) ? 'player' : 'landing';
}

function syncUrl(videoId: string | null, view: AppView): void {
  if (typeof window === 'undefined') return;
  const base =
    window.location.pathname
      .replace(/\/cards\/?$/, '')
      .replace(/\/style-studio\/?$/, '')
      .replace(/\/login\/callback\/?$/, '') || '/';
  let next: string;
  if (view === 'login-callback') {
    next = `${base === '/' ? '' : base}/login/callback`.replace(/\/+/g, '/') || '/login/callback';
  } else if (view === 'style-studio') {
    next = `${base === '/' ? '' : base}/style-studio`.replace(/\/+/g, '/') || '/style-studio';
  } else if (view === 'cards') {
    next = `${base === '/' ? '' : base}/cards`.replace(/\/+/g, '/') || '/cards';
  } else if (videoId) {
    next = `${base}${buildVideoSearch(videoId)}`;
  } else {
    next = base;
  }
  const current = `${window.location.pathname}${window.location.search}`;
  if (next === current) return;
  window.history.pushState(
    view === 'cards' || view === 'style-studio' || view === 'login-callback'
      ? { view }
      : videoId
        ? { v: videoId }
        : {},
    '',
    next,
  );
}

export function App({
  playerOptions,
  initialSearch,
  initialProviderKind,
  initialView,
  settingsStore: settingsProp,
}: AppProps = {}) {
  const search =
    initialSearch ?? (typeof window !== 'undefined' ? window.location.search : '');
  const defaultSettings = useState(() => new LocalStorageSettingsStore({ host: null }))[0];
  const settingsStore = settingsProp ?? defaultSettings;
  useThemeFromSettings(settingsStore);
  const [videoId, setVideoId] = useState<string | null>(() => readVideoIdFromSearch(search));
  const [view, setView] = useState<AppView>(() => initialView ?? readViewFromLocation());
  const [returnVideoId, setReturnVideoId] = useState<string | null>(null);
  const providerKind =
    initialProviderKind ?? readProviderFromSearch(search) ?? 'sora';

  useEffect(() => {
    const onPop = () => {
      setView(readViewFromLocation());
      setVideoId(readVideoIdFromSearch(window.location.search));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const openVideo = useCallback((id: string) => {
    pushRecentVideo(id);
    setVideoId(id);
    setView('player');
    syncUrl(id, 'player');
  }, []);

  const closeVideo = useCallback(() => {
    setVideoId(null);
    setView('landing');
    syncUrl(null, 'landing');
  }, []);

  const openCards = useCallback(() => {
    setView('cards');
    syncUrl(null, 'cards');
  }, []);

  const closeCards = useCallback(() => {
    if (videoId) {
      setView('player');
      syncUrl(videoId, 'player');
    } else {
      setView('landing');
      syncUrl(null, 'landing');
    }
  }, [videoId]);

  const openStyleStudio = useCallback(() => {
    setReturnVideoId(videoId);
    setView('style-studio');
    syncUrl(null, 'style-studio');
  }, [videoId]);

  const closeStyleStudio = useCallback(() => {
    const backId = returnVideoId ?? videoId;
    if (backId) {
      setVideoId(backId);
      setView('player');
      syncUrl(backId, 'player');
    } else {
      setView('landing');
      syncUrl(null, 'landing');
    }
  }, [returnVideoId, videoId]);

  const closeLoginCallback = useCallback(() => {
    setView('landing');
    syncUrl(null, 'landing');
  }, []);

  if (view === 'login-callback') {
    return <LoginCallbackPage settingsStore={settingsStore} onDone={closeLoginCallback} />;
  }

  if (view === 'style-studio') {
    return <StyleStudioPage settingsStore={settingsStore} onBack={closeStyleStudio} />;
  }

  if (view === 'cards') {
    return <CardsPage settingsStore={settingsStore} onBack={closeCards} />;
  }

  if (videoId) {
    return (
      <Player
        videoId={videoId}
        onBack={closeVideo}
        playerOptions={playerOptions}
        initialProviderKind={providerKind}
        settingsStore={settingsStore}
        onOpenCards={openCards}
        onOpenStyleStudio={openStyleStudio}
      />
    );
  }

  return <Landing onOpenVideo={openVideo} />;
}
